import type { GetPublicProductConfigOutput } from 'korint-sdk';

type SignatureConfig = GetPublicProductConfigOutput['signature'];

// `page` is typed loosely (Record<string, any>) by the generated schema but is
// really `number | 'LATEST'`. Keep positive integers; drop 'LATEST'/NaN — those
// auto-target the last page and impose no minimum.
const toPageNumber = (page: unknown): number | undefined => {
  const value = typeof page === 'number' ? page : Number(page);
  return Number.isFinite(value) && value > 0 ? value : undefined;
};

/**
 * Minimum number of pages an uploaded document of `documentType` must have so
 * that every coordinate-anchored signature field lands on a page that exists.
 *
 * Derived from the signature config (the max fixed page across anchors,
 * mentions, texts and checkboxes of the matching SIGN entry). Returns
 * `undefined` when no fixed numeric page is configured — e.g. signing via
 * `page: 'LATEST'` or via in-PDF Yousign tags — meaning there is no minimum to
 * enforce.
 */
export function getMinSignaturePages(
  signature: SignatureConfig | undefined,
  documentType: string | undefined,
): number | undefined {
  if (!signature || !documentType) return undefined;

  const pages: number[] = [];
  for (const doc of signature.signedDocuments ?? []) {
    if (doc.type !== documentType || doc.signature !== 'SIGN') continue;

    const fields = [
      doc.anchor,
      ...(doc.mentions ?? []),
      ...(doc.texts ?? []),
      ...(doc.checkboxes ?? []),
      ...(doc.signers ?? []).flatMap(signer => [
        signer.anchor,
        ...(signer.mentions ?? []),
        ...(signer.texts ?? []),
        ...(signer.checkboxes ?? []),
      ]),
    ];

    for (const field of fields) {
      const page = toPageNumber(field?.page);
      if (page !== undefined) pages.push(page);
    }
  }

  return pages.length > 0 ? Math.max(...pages) : undefined;
}
